import { CoachOverallGradeBadge } from '@/components/coaches/coach-overall-grade-badge';
import { MultiSelectOption as LibraryMultiSelectOption, MultiSelectOption } from '@/components/ui/multi-select';
import {
  BackgroundCheckStatus,
  BackgroundCheckStatusLabelMap,
  ClassEligibility,
  ClassEligibilityLabelMap,
  CoachOverallGrade,
  CoachSessionRemarkV2,
  CoachSessionRemarkV2CannotBeAdded,
  CoachSessionRemarkV2CannotBeRemoved,
  CoachSessionRemarkV2LabelMap,
  CoachUser,
  EmailsSent,
  EmailsSentLabelMap,
  TechCheckType,
  TechCheckTypeLabelMap,
  UserLike,
} from '@airreading/common/data';
import { CheckedState } from '@radix-ui/react-checkbox';
import { ReactNode, useEffect, useState } from 'react';
import { z } from 'zod';

/**
 * DO NOT IMPORT
 * Used by multi-select component only
 */
export function useDebounce<T>(value: T, delay?: number): T {
  const [debouncedValue, setDebouncedValue] = useState<T>(value);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedValue(value), delay || 500);

    return () => {
      clearTimeout(timer);
    };
  }, [value, delay]);

  return debouncedValue;
}

export type Fetcher<T> = (typeahead: string) => T | Promise<T>;
export type OptionsOrFetcher<T> = T | Fetcher<T>;

/**
 * Represent one select option in the form of label and value
 */
export const selectOption = z.object({
  label: z.string(),
  value: z.string(),
  disable: z.boolean().optional(),
  className: z.string().optional(),
});
export const requiredSelectOption = (errorMessage: string = 'Required') =>
  selectOption.refine(
    ({ value }) => {
      return value !== '' && value !== undefined && value !== null;
    },
    {
      message: errorMessage,
    },
  );
export type SelectOption = z.infer<typeof selectOption>;
export const emptySelectOption = { label: '', value: '', disable: false };

/**
 * Represent select option type used by `multi-select.tsx` and `form-multi-select.tsx`
 */
export const multiSelectOptions = z.array(selectOption);
export const requiredMultiSelectOptions = (errorMessage: string = 'Required') =>
  multiSelectOptions.refine(
    (selections) =>
      selections.length > 0 && selections.every((s) => s.value !== '' && s.value !== undefined && s.value !== null),
    {
      message: errorMessage,
    },
  );

export const multiSelectOptionsHaveEmptyValue = (options: z.infer<typeof multiSelectOptions>) =>
  options.length === 0 || options.some((o) => !o.value);
export const multiSelectOptionsHaveNoEmptyValue = (options: z.infer<typeof multiSelectOptions>) =>
  !multiSelectOptionsHaveEmptyValue(options);

export const multiSelectLabelFilter = (options: LibraryMultiSelectOption[] = [], caseSensitive = false) => {
  return async (input: string) => {
    if (!input) {
      return options;
    }

    if (!caseSensitive) {
      input = input.toLowerCase();
    }

    return options.filter((o) => (caseSensitive ? o.label : o.label.toLowerCase()).includes(input));
  };
};

export const comboBoxOptionsOf = (optionsOrOptionsFetcher: OptionsOrFetcher<{ label: string; value: string }[]>) => {
  return {
    options: optionsOrOptionsFetcher,
    optionHash: (option: LibraryMultiSelectOption | undefined) => (option ? option.value : 'undefined option'),
    optionToFilterableValues: (option: LibraryMultiSelectOption | undefined) => (option ? [option.label] : []),
    renderOption: generateOptionLabel,
  };
};

export function generateOptionLabel(option?: LibraryMultiSelectOption): ReactNode | string {
  if (!option) {
    return null;
  }

  if (option.rightText) {
    return (
      <div className="flex justify-between items-center w-full gap-2">
        <span className="truncate">{option.label}</span>
        {option.rightText}
      </div>
    );
  }

  return option.label;
}

export const buildIntArray = (from: number, to: number, step = 1): number[] => {
  const result: number[] = [];
  for (let i = from; i <= to; i += step) {
    result.push(i);
  }
  return result;
};

export const buildUserSelectOptions = ({
  id,
  fullNameOrEmail,
}: Pick<UserLike, 'id' | 'fullNameOrEmail'>): LibraryMultiSelectOption => ({
  label: fullNameOrEmail ?? '',
  value: id,
});

export const buildCoachSelectOptions = ({
  id,
  fullNameOrEmail,
  overallGrade,
  studentGroupCount,
}: CoachUser & { studentGroupCount?: number | null }): LibraryMultiSelectOption => {
  const classCount = studentGroupCount ?? 0;
  const classNameBadge =
    overallGrade === CoachOverallGrade.Never_Taught || overallGrade === CoachOverallGrade.Not_reviewed
      ? 'text-xs'
      : undefined;
  const rightText = (
    <div className="flex items-center gap-1.5 shrink-0">
      <CoachOverallGradeBadge grade={overallGrade} size="sm" className={classNameBadge} />
      <span className="text-muted-foreground text-xs tabular-nums">{classCount}</span>
    </div>
  );

  return {
    label: fullNameOrEmail ?? '',
    value: id,
    rightText,
  };
};

export const buildNameLikeSelectOptions = ({ id, name }: { id: string; name: string }): LibraryMultiSelectOption => ({
  label: name,
  value: id,
});

interface BooleanSelectOptions {
  trueLabel?: string;
  falseLabel?: string;
  trueStyle?: string;
  falseStyle?: string;
}

export function buildBooleanSelectOptions(config: BooleanSelectOptions = {}): MultiSelectOption[] {
  const { trueLabel = 'Yes', falseLabel = 'No', trueStyle = '', falseStyle = '' } = config;
  return [
    { value: 'true', label: trueLabel, className: trueStyle },
    { value: 'false', label: falseLabel, className: falseStyle },
  ];
}

export function buildSameValueLabelOptions(values: string[]): MultiSelectOption[] {
  return values.map((value) => ({
    value,
    label: value,
  }));
}

export const buildClassEligibilitySelectOption = (eligibility: ClassEligibility): LibraryMultiSelectOption => ({
  value: eligibility,
  label: ClassEligibilityLabelMap[eligibility],
});

export const buildBackgroundCheckStatusSelectOption = (status: BackgroundCheckStatus): LibraryMultiSelectOption => ({
  value: status,
  label: BackgroundCheckStatusLabelMap[status],
});

export const buildTechCheckTypeSelectOption = (tech: TechCheckType): LibraryMultiSelectOption => ({
  value: tech,
  label: TechCheckTypeLabelMap[tech],
});

export const booleanOnlyValueOf = (checkedState: CheckedState) => {
  const value = checkedState.valueOf();
  if (typeof value === 'string') return false;
  return value;
};

export const buildSessionRemarksV2Options = ({
  options,
  selected,
}: {
  options?: CoachSessionRemarkV2[];
  selected?: CoachSessionRemarkV2[];
}): LibraryMultiSelectOption[] => {
  const coachSessionRemarkV2CannotBeAdded = [...CoachSessionRemarkV2CannotBeAdded];

  if (selected?.includes(CoachSessionRemarkV2.subClass)) {
    coachSessionRemarkV2CannotBeAdded.push(CoachSessionRemarkV2.subClassWithin12Hours);
  }

  if (selected?.includes(CoachSessionRemarkV2.subClassWithin12Hours)) {
    coachSessionRemarkV2CannotBeAdded.push(CoachSessionRemarkV2.subClass);
  }

  return (options || Object.values(CoachSessionRemarkV2)).map((tag) => {
    const option: LibraryMultiSelectOption = {
      value: tag,
      label: CoachSessionRemarkV2LabelMap[tag],
      disable: coachSessionRemarkV2CannotBeAdded.includes(tag),
      fixed: CoachSessionRemarkV2CannotBeRemoved.includes(tag),
    };
    return option;
  });
};

export const buildEmailsSentSelectOption = (email: EmailsSent): LibraryMultiSelectOption => ({
  value: email,
  label: EmailsSentLabelMap[email],
});
